import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Skeleton,
  Alert,
  Button,
  Avatar,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import { useQuery } from "../../hooks/useQuery";
import { fetchSubjectAnalysis } from "../../utils/analysisApi";

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.68)",
  border: "1px solid rgba(255,255,255,0.88)",
  boxShadow:
    "0 8px 32px rgba(16,185,129,0.09), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const AmbientBackground = () => (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      overflow: "hidden",
      background:
        "linear-gradient(160deg, #f0fff7 0%, #fafbff 50%, #f0f4ff 100%)",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "-80px",
        right: "-80px",
        width: 420,
        height: 420,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: "10%",
        left: "-60px",
        width: 340,
        height: 340,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage:
          "radial-gradient(circle, rgba(16,185,129,0.06) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
        maskImage:
          "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
      }}
    />
  </Box>
);

const DIFFICULTY_CONFIG = {
  Easy: {
    color: "#10b981",
    bg: "rgba(16,185,129,0.1)",
    gradient: "linear-gradient(135deg, #10b981, #34d399)",
  },
  Medium: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    gradient: "linear-gradient(135deg, #f59e0b, #fbbf24)",
  },
  Hard: {
    color: "#ef4444",
    bg: "rgba(239,68,68,0.08)",
    gradient: "linear-gradient(135deg, #ef4444, #f87171)",
  },
};

const DIST_COLORS = ["#ef4444", "#f59e0b", "#fbbf24", "#6366f1", "#10b981"];

const DistTooltip = ({ active, payload, label }) => {
  if (active && payload?.[0]) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "#a5b4fc",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.4,
          }}
        >
          Score {label}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.3rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {payload[0].value} students
        </Typography>
      </Box>
    );
  }
  return null;
};

const TrendTooltip = ({ active, payload, label }) => {
  if (active && payload?.[0]) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #022c22 0%, #064e3b 100%)",
          border: "1px solid rgba(16,185,129,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "#6ee7b7",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.4,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.3rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {payload[0].value?.toFixed(1)}%
        </Typography>
      </Box>
    );
  }
  return null;
};

const StatBox = ({ label, value, gradient, glowColor, icon, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.88 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
    style={{ flex: "1 1 140px" }}
  >
    <MotionBox
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      sx={{
        p: 2.5,
        borderRadius: "20px",
        ...glassMixin,
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        cursor: "default",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: gradient,
        }}
      />
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          background: gradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          mx: "auto",
          mb: 1.5,
          boxShadow: `0 6px 16px ${glowColor}`,
          "& svg": { fontSize: 22 },
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "1.75rem",
          fontWeight: 800,
          color: "#111827",
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.68rem",
          color: "#9ca3af",
          fontWeight: 700,
          mt: 0.4,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
    </MotionBox>
  </motion.div>
);

const PageSkeleton = () => (
  <Box sx={{ position: "relative", minHeight: "100vh" }}>
    <AmbientBackground />
    <Container maxWidth="lg" sx={{ py: 4, position: "relative", zIndex: 1 }}>
      <Skeleton width={160} height={36} sx={{ mb: 3 }} />
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            sx={{ flex: "1 1 120px", height: 108, borderRadius: "20px" }}
          />
        ))}
      </Box>
      <Skeleton
        variant="rounded"
        width="100%"
        height={290}
        sx={{ borderRadius: "24px", mb: 3 }}
      />
      <Skeleton
        variant="rounded"
        width="100%"
        height={270}
        sx={{ borderRadius: "24px" }}
      />
    </Container>
  </Box>
);

export default function SubjectAnalysisPage() {
  const { id } = useParams();
  const query = useQuery();
  const navigate = useNavigate();
  const term = query.get("term") || "";
  const session = query.get("session") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchSubjectAnalysis(id, term, session)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [id, term, session]);

  if (loading) return <PageSkeleton />;
  if (error)
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: "20px" }}
          action={
            <Button onClick={() => navigate(-1)} size="small">
              Go Back
            </Button>
          }
        >
          Failed to load subject analysis.
        </Alert>
      </Container>
    );
  if (!data) return <PageSkeleton />;

  const distributionData = Object.entries(data.distribution || {}).map(
    ([range, count]) => ({ range, count })
  );
  const trendData = (data.trend || []).map((t) => ({
    date: t.date,
    avg: parseFloat(t.avg || 0),
  }));
  const diffConf =
    DIFFICULTY_CONFIG[data.difficulty] || DIFFICULTY_CONFIG["Medium"];

  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      <AmbientBackground />
      <Container maxWidth="lg" sx={{ py: 4, position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: "#4b5563",
              fontWeight: 700,
              px: 2,
              background: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "12px",
              backdropFilter: "blur(12px)",
              "&:hover": { background: "rgba(255,255,255,0.95)" },
            }}
          >
            Back
          </Button>
        </motion.div>

        {/* header */}
        <MotionBox
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "18px",
                background: "linear-gradient(135deg, #10b981 0%, #6366f1 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(16,185,129,0.4)",
              }}
            >
              <MenuBookRoundedIcon sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 800,
                  color: "#111827",
                  letterSpacing: "-0.04em",
                }}
              >
                {data.subject}
              </Typography>
              <Stack direction="row" spacing={1} mt={0.5}>
                <Chip
                  label={`Difficulty: ${data.difficulty || "N/A"}`}
                  size="small"
                  sx={{
                    background: diffConf.bg,
                    color: diffConf.color,
                    fontWeight: 700,
                    border: `1px solid ${diffConf.color}30`,
                    borderRadius: "8px",
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </MotionBox>

        {/* stats */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <StatBox
            label="Average Score"
            value={`${parseFloat(data.average_score || 0).toFixed(1)}%`}
            gradient="linear-gradient(135deg, #10b981, #34d399)"
            glowColor="rgba(16,185,129,0.35)"
            icon={<AutoGraphRoundedIcon />}
            index={0}
          />
          <StatBox
            label="Pass Rate"
            value={`${parseFloat(data.pass_rate || 0).toFixed(1)}%`}
            gradient="linear-gradient(135deg, #6366f1, #818cf8)"
            glowColor="rgba(99,102,241,0.35)"
            icon={<BarChartRoundedIcon />}
            index={1}
          />
          <StatBox
            label="Difficulty"
            value={data.difficulty || "N/A"}
            gradient={diffConf.gradient}
            glowColor={`${diffConf.color}55`}
            icon={<SpeedRoundedIcon />}
            index={2}
          />
        </Box>

        {/* score distribution */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          sx={{
            mb: 3,
            borderRadius: "24px",
            overflow: "hidden",
            ...glassMixin,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #8b5cf6, #6366f1)",
            }}
          />
          <CardContent sx={{ p: "24px !important" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <BarChartRoundedIcon sx={{ color: "#8b5cf6", fontSize: 22 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Score Distribution
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", fontSize: "0.78rem", mb: 3 }}
            >
              Number of students in each score bracket
            </Typography>
            {distributionData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.disabled">
                  No distribution data
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={distributionData}
                  margin={{ top: 20, right: 8, left: -16, bottom: 0 }}
                  barSize={38}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.05)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="range"
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#4b5563" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<DistTooltip />}
                    cursor={{ fill: "rgba(99,102,241,0.05)", rx: 6 }}
                  />
                  <Bar dataKey="count" radius={[10, 10, 0, 0]}>
                    {distributionData.map((_, i) => (
                      <Cell
                        key={i}
                        fill={DIST_COLORS[i % DIST_COLORS.length]}
                        fillOpacity={0.88}
                      />
                    ))}
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fontSize: 11, fontWeight: 700, fill: "#4b5563" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </MotionCard>

        {/* performance trend */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.27 }}
          sx={{
            mb: 3,
            borderRadius: "24px",
            overflow: "hidden",
            ...glassMixin,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #10b981, #34d399)",
            }}
          />
          <CardContent sx={{ p: "24px !important" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
              <AutoGraphRoundedIcon sx={{ color: "#10b981", fontSize: 22 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Performance Trend
              </Typography>
            </Stack>
            {trendData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.disabled">No trend data</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart
                  data={trendData}
                  margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="subjectGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#10b981"
                        stopOpacity={0.22}
                      />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.05)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<TrendTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="avg"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#subjectGrad)"
                    dot={{ fill: "#10b981", r: 3.5, strokeWidth: 0 }}
                    activeDot={{
                      r: 7,
                      fill: "#10b981",
                      stroke: "#fff",
                      strokeWidth: 2.5,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </MotionCard>

        {/* top student */}
        {data.top_student && (
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.33 }}
            sx={{ borderRadius: "24px", overflow: "hidden", ...glassMixin }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
              }}
            />
            <CardContent sx={{ p: "20px !important" }}>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "11px",
                    background: "rgba(245,158,11,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(245,158,11,0.2)",
                  }}
                >
                  <EmojiEventsRoundedIcon
                    sx={{ color: "#f59e0b", fontSize: 20 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Syne', sans-serif",
                    fontSize: "0.66rem",
                    fontWeight: 700,
                    color: "#9ca3af",
                    letterSpacing: "0.1em",
                  }}
                >
                  TOP PERFORMER
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    fontWeight: 800,
                    background: "linear-gradient(135deg, #f59e0b, #d97706)",
                    boxShadow: "0 4px 14px rgba(245,158,11,0.45)",
                    fontSize: "1rem",
                    fontFamily: "'Syne', sans-serif",
                  }}
                >
                  {(data.top_student?.name || "?")
                    .split(" ")
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Syne', sans-serif",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {data.top_student?.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                    Best performer in this subject
                  </Typography>
                </Box>
                <Chip
                  label={`${data.top_student?.score}%`}
                  size="small"
                  sx={{
                    background: "rgba(245,158,11,0.1)",
                    color: "#f59e0b",
                    fontWeight: 800,
                    fontSize: "0.9rem",
                    height: 32,
                    border: "1px solid rgba(245,158,11,0.25)",
                    px: 0.5,
                    borderRadius: "10px",
                    fontFamily: "'Syne', sans-serif",
                  }}
                />
              </Stack>
            </CardContent>
          </MotionCard>
        )}
      </Container>
    </Box>
  );
}
